import React from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";

// Decodifica polyline codificada do Google (algoritmo padrao).
function decodePolyline(str) {
  if (!str) return [];
  let index = 0, lat = 0, lng = 0;
  const coords = [];
  while (index < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    coords.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return coords;
}

// Desenha a polyline via API imperativa (dentro de um filho do <Map>).
function RoutePath({ encoded }) {
  const map = useMap();
  React.useEffect(() => {
    if (!map || !encoded || !window.google) return undefined;
    const path = decodePolyline(encoded);
    if (!path.length) return undefined;
    const line = new window.google.maps.Polyline({
      path, strokeColor: "#B5179E", strokeOpacity: 0.9, strokeWeight: 4,
    });
    line.setMap(map);
    return () => line.setMap(null);
  }, [map, encoded]);
  return null;
}

/**
 * RouteMap - origem + paradas numeradas + rota (polyline) quando disponivel.
 * Fallback: Google desabilitado / sem chave / sem coordenadas -> placeholder.
 */
export default function RouteMap({ apiKey, enabled = false, origin, stops = [], polyline }) {
  const points = stops
    .map((s, i) => ({ ...s, order: s.order || {}, idx: i + 1 }))
    .filter((s) => (s.order.address_lat != null && s.order.address_lng != null));

  if (!enabled || !apiKey) {
    return (
      <div className="flex h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-[#FBFAFD] text-center" data-testid="route-map-disabled">
        <MapPin size={26} style={{ color: PURPLE }} className="mb-2" />
        <p className="text-sm font-bold text-[#6B5A69]">Mapa indisponível</p>
        <p className="mt-1 max-w-xs text-[12px] text-[#9A8A98]">Configure o Google Maps (GOOGLE_MAPS_ENABLED) para visualizar as paradas no mapa. A ordem manual continua funcionando.</p>
      </div>
    );
  }

  const center = (origin && origin.lat != null) ? { lat: origin.lat, lng: origin.lng }
    : points.length ? { lat: points[0].order.address_lat, lng: points[0].order.address_lng }
      : { lat: -23.55, lng: -46.63 };

  return (
    <div className="overflow-hidden rounded-2xl border border-border" data-testid="route-map">
      <APIProvider apiKey={apiKey}>
        <Map style={{ width: "100%", height: "320px" }} defaultCenter={center} defaultZoom={12}
             mapId="cris-rota-map" gestureHandling="greedy" disableDefaultUI={true} zoomControl={true}>
          {origin && origin.lat != null && (
            <AdvancedMarker position={{ lat: origin.lat, lng: origin.lng }}>
              <div className="grid h-7 w-7 place-items-center rounded-full text-[11px] font-bold text-[#2B1E4A]" style={{ background: GOLD, border: "2px solid #fff" }}>B</div>
            </AdvancedMarker>
          )}
          {points.map((s) => (
            <AdvancedMarker key={s.id} position={{ lat: s.order.address_lat, lng: s.order.address_lng }}>
              <div className="grid h-7 w-7 place-items-center rounded-full text-[12px] font-bold text-white" style={{ background: PURPLE, border: "2px solid #fff" }}>{s.idx}</div>
            </AdvancedMarker>
          ))}
          <RoutePath encoded={polyline} />
        </Map>
      </APIProvider>
    </div>
  );
}
